import i18n from './i18n';
import { parse, isValid } from 'date-fns'

/**
 * Validation Rule for required fields (null and false are not valid)
 * @param {string} value
 * @returns a function to validate an form field
 */
export const field_required = value => !!value || i18n.t('field_required');

/**
 * Validation Rule for boolean values
 * @param {boolean} value
 * @returns a function to validate an form field
 */
 export const field_boolean = value => typeof value === 'boolean' || i18n.t('field_boolean');


/**
 * Validation Rule for check if a value is true (a boolean and true)
 * @param {boolean} value
 * @param {string} errorMessage (optional) an custom error message
 * @returns a function to validate an form field
 */
export const field_true = (value,errorMessage) => (!!value && value===true) || (errorMessage ? errorMessage : i18n.t('field_true'));


/**
 * Validation Rule for min length of string-fields
 * @param {string} value
 * @param {number} min
 * @returns a function to validate an form field
 */
export const field_min_length = (value, min) => (!value || (!!value && typeof value=== 'string' && value.length >= min)) || i18n.tc('field_min_length', min);


/**
 * Validation Rule for max length of string-fields
 * @param {string} value
 * @param {number} max
 * @returns a function to validate an form field
 */
export const field_max_length = (value, max) => (!value || (!!value && typeof value=== 'string' && value.length <= max)) || i18n.tc('field_max_length', max);


/**
 * Validation Rule for min length of arrays.
 * @param {array} value
 * @param {number} minLength
 * @returns a function to validate an form field
 */
export const field_min_array_length = (value, minLength) => (!!value && typeof value==='object' && value.length >= minLength) || i18n.tc('field_min_array_length', minLength);


/**
 * Validation Rule for max length of arrays.
 * @param {array} value
 * @param {number} maxLength
 * @returns a function to validate an form field
 */
export const field_max_array_length = (value, maxLength) => (!value || (!!value && typeof value==='object' && value.length <= maxLength)) || i18n.tc('field_max_array_length', maxLength);



/**
 * Validation rule for emails
 * @param {string} value the value to be checked
 * @returns {boolean} true if the test has passed
 */
export const field_email = (value, errorMessage) => {
  if(!value || typeof value !== 'string' || value.trim()==='') return true;
  const regexPattern = /^(?!.*(?:''|\.\.))(([^<>()\[\]\\,;:\s@"]+(\.[^<>()\[\]\\,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;// eslint-disable-line no-useless-escape
  return regexPattern.test(value) || (errorMessage ? errorMessage : i18n.t('field_email'));
}

export const field_date_de = (value, errorMessage) => {
  if(!value || value.trim()==='') return true;
  let result = true;

  //check length (needed as the next step will generate a valid date even if the year is to short)
  if(result && value.length!==10) result = false;

  const date = parse(value, 'dd.MM.yyyy', new Date());
  if(result && !isValid(date)) result = false;

  return result || (errorMessage ? errorMessage : i18n.t('field_date'));
}



/**
 * Validation rule for urls (i.e. "https://www.google.de/?param=test#start")
 * @param {string} value the value to be checked
 * @param {string} errorMessage (optional) an custom error message
 * @returns {boolean|string} true if the test has passed, error message if it fails
 */
export const field_url = (value, errorMessage) => {
    if(!value || typeof value !== 'string' || value.trim()==='') return true;
    var regexPattern = new RegExp(
        '^(https?:\\/\\/)'+ // protocol
        '((([\u00C0-\u017Fa-z\\d]([\u00C0-\u017Fa-z\\d-]*[\u00C0-\u017Fa-z\\d])*)\\.)+[\u00C0-\u017Fa-z]{2,}|'+ // domain name
        '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
        '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
        '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
        '(\\#[-a-z\\d_]*)?$','i'
    );
    return regexPattern.test(value) || (errorMessage ? errorMessage : i18n.t('field_url'));
}


/**
 * Validation rule for passwords.
 * @param {string} value the value to be checked
 * @returns {boolean|string} true if the test has passed, error message if it fails
 */
export const field_password = (value) => {
    if(!value || value.trim()==='') return true;
    if(field_min_length(value, 6 )!==true) return field_min_length(value, 6 );
    if(field_max_length(value, 50)!==true) return field_max_length(value, 50);
    return true;
}


/**
 * Validation for postalcodes
 * @param {string} value the value to be checked
 * @returns {boolean|string} true if the test has passed, error message if it fails
 */
export const field_postalcode = value => {
    const regexPattern = /[0-9]{5}/;
    return value !== null && regexPattern.test(value) && value.length === 5 || i18n.t('field_rule_plz');
}

/**
 * Validation for phone numbers
 * @param {string} value the value to be checked
 * @returns {boolean|string} true if the test has passed, error message if it fails
 */
export const field_phone = value => {
    const regexPattern = /^\+(?:[0-9] ?){6,14}[0-9]$/;
    return value !== null && regexPattern.test(value) || i18n.t('field_rule_phone');
}

/**
 * Space out an string after x digits with a fillCharacter
 * @see Source: https://stackoverflow.com/questions/17260238/how-to-insert-space-every-4-characters-for-iban-registering
 * @param {string} str
 * @param {number} afterXDigits (optional, default=4)
 * @param {string} fillCharacter (optional, default=" ")
 * @returns
 */
export const spacify = (str, afterXDigits, fillCharacter) => {
    if (!str) return false;
    afterXDigits = afterXDigits || 4;
    fillCharacter = fillCharacter || " ";
    var v = str.replace(/[^\dA-Z]/g, ''),
        reg = new RegExp(".{" + afterXDigits + "}", "g");
    return v.replace(reg, function (a) {
        return a + fillCharacter;
    }).replace(/[^0-9]+$/, "");
}





/**
 * Validation rule for the max file size of single files
 * @param {array} files the value to be checked
 * @param {numeric} maxFileSize  the size to be checked
 * @param {string} errorMessage (optional) an custom error message
 * @returns {boolean} true if the test has passed
 */
export const files_max_size_single = (files, maxFileSize, errorMessage) => {
  if((typeof maxFileSize !== 'number') || maxFileSize <= 0) throw new Error('The files_max_size_single-rule is not correctly set up.');
  if(!Array.isArray(files)) return true;
  let foundToLargeFile = false;
  for (let index = 0; index < files.length; index++) {
    if(files[index].size > maxFileSize) {
      foundToLargeFile = index;
      break;
    }
  }
  return (foundToLargeFile===false) || (errorMessage ? errorMessage : i18n.t('fileManagementError_maxFileSizeSingle', humanFileSize(maxFileSize)));
}

/**
 * Validation rule for the maximum size in the sum of all files
 * @param {array} files the value to be checked
 * @param {numeric} maxFileSize the size to be checked
 * @param {string} errorMessage (optional) an custom error message
 * @returns {boolean} true if the test has passed
 */
export const files_max_size_sum = (files, maxFileSize, errorMessage) => {
  if((typeof maxFileSize !== 'number' || maxFileSize <= 0)) throw new Error('The files_max_size_sum-rule is not correctly set up.');
  if(!Array.isArray(files)) return true;
  const sumFileSizes = files.reduce((previousValue, elem) => previousValue + elem.size, 0);
  return (sumFileSizes <= maxFileSize) || (errorMessage ? errorMessage : i18n.t('fileManagementError_maxFileSizeSum', humanFileSize(maxFileSize)));
}

/**
 * Validation rule for allowed file mime-types
 * @param {array} files the value to be checked
 * @param {array} allowedMimeTypes the list of all allowed mime types
 * @param {string} errorMessage (optional) an custom error message
 * @returns {boolean} true if the test has passed
 */
export const files_only_allowed_mime_types = (files, allowedMimeTypes, errorMessage) => {
  if(!Array.isArray(allowedMimeTypes)) throw new Error('The files_only_allowed_mime_types is not correctly set up.');
  if(!Array.isArray(files)) return true;
  return !files.find(file => !allowedMimeTypes.includes(file.type)) || (errorMessage ? errorMessage : i18n.t('fileManagementError_wrongMimeType'));
}

/**
 * Validation rule for the maximum count of allowed files
 * @param {array} files the value to be checked
 * @param {numeric} maxFileCount number of allowed files
 * @param {string} errorMessage (optional) an custom error message
 * @returns {boolean} true if the test has passed
 */
export const files_max_file_count = (files, maxFileCount, errorMessage) => {
  if((typeof maxFileCount !== 'number') || maxFileCount <= 0) throw new Error('The files_max_file_count-rule is not correctly set up.');
  if(!Array.isArray(files)) return true;
  return (files.length <= maxFileCount) || (errorMessage ? errorMessage : i18n.tc('fileManagementError_toManyFiles', maxFileCount));
}